/**
 * Centralized rower color management for consistent colors across all charts
 * Ensures that Rower 1 always has the same color in every chart, Rower 2 has the same color, etc.
 */

// GLYYD brand colors for rowers - consistent across all charts
// Following design standard color palette from GLYYD_COACH_DASHBOARD_DESIGN_STANDARD.md
export const ROWER_COLORS = [
  '#1E40AF', // Rowing blue (design standard) - Rower 1
  '#DC2626', // Rowing red (design standard) - Rower 2
  '#3B82F6', // Rowing sky (design standard) - Rower 3
  '#991B1B', // Rowing crimson (design standard) - Rower 4
  '#10B981', // Green (semantic color) - Rower 5
  '#F59E0B', // Orange/Yellow (semantic color) - Rower 6
  '#8B5CF6', // Purple - Rower 7
  '#EC4899', // Pink - Rower 8
] as const;

// RGB versions for charts that need RGB format
export const ROWER_COLORS_RGB = [
  'rgb(30, 64, 175)', // Rowing blue (design standard) - Rower 1
  'rgb(220, 38, 38)', // Rowing red (design standard) - Rower 2
  'rgb(59, 130, 246)', // Rowing sky (design standard) - Rower 3
  'rgb(153, 27, 27)', // Rowing crimson (design standard) - Rower 4
  'rgb(16, 185, 129)', // Green (semantic color) - Rower 5
  'rgb(245, 158, 11)', // Orange/Yellow (semantic color) - Rower 6
  'rgb(139, 92, 246)', // Purple - Rower 7
  'rgb(236, 72, 153)', // Pink - Rower 8
] as const;

/**
 * Get color for a specific rower by index
 * @param rowerIndex - Zero-based index of the rower (0 = Rower 1, 1 = Rower 2, etc.)
 * @param format - Color format to return ('hex' or 'rgb')
 * @returns Color string in the requested format
 */
export const getRowerColor = (rowerIndex: number, format: 'hex' | 'rgb' = 'hex'): string => {
  const colors = format === 'rgb' ? ROWER_COLORS_RGB : ROWER_COLORS;
  return colors[rowerIndex % colors.length];
};

/**
 * Get background color (with transparency) for a specific rower
 * @param rowerIndex - Zero-based index of the rower
 * @param opacity - Opacity value (0-1), defaults to 0.2
 * @returns RGBA color string
 */
export const getRowerBackgroundColor = (rowerIndex: number, opacity: number = 0.2): string => {
  const rgbColor = getRowerColor(rowerIndex, 'rgb');
  return rgbColor.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
};

/**
 * Get an array of colors for multiple rowers
 * @param count - Number of colors needed
 * @param format - Color format to return ('hex' or 'rgb')
 * @returns Array of color strings
 */
export const getRowerColors = (count: number, format: 'hex' | 'rgb' = 'hex'): string[] => {
  return Array.from({ length: count }, (_, index) => getRowerColor(index, format));
};

/**
 * Parse rower name/key to get consistent index
 * Handles various rower naming conventions: "Rower 1", "rower1", "1", etc.
 * @param rowerKey - Rower identifier string
 * @returns Zero-based rower index
 */
export const parseRowerIndex = (rowerKey: string): number => {
  // Handle null, undefined, or empty strings
  if (!rowerKey || typeof rowerKey !== 'string') {
    return 0; // Default to first color
  }

  // Extract number from rower key (handles "Rower 1", "rower1", "1", etc.)
  const match = rowerKey.match(/(\d+)/);
  if (match) {
    return parseInt(match[1], 10) - 1; // Convert to zero-based index
  }

  // Fallback: use hash of the key for consistent but arbitrary assignment
  let hash = 0;
  for (let i = 0; i < rowerKey.length; i++) {
    const char = rowerKey.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash) % ROWER_COLORS.length;
};

/**
 * Get color for a rower by their key/name
 * @param rowerKey - Rower identifier (e.g., "Rower 1", "rower2", etc.)
 * @param format - Color format to return ('hex' or 'rgb')
 * @returns Color string in the requested format
 */
export const getRowerColorByKey = (rowerKey: string, format: 'hex' | 'rgb' = 'hex'): string => {
  const index = parseRowerIndex(rowerKey);
  return getRowerColor(index, format);
};

/**
 * Get background color for a rower by their key/name
 * @param rowerKey - Rower identifier
 * @param opacity - Opacity value (0-1), defaults to 0.2
 * @returns RGBA color string
 */
export const getRowerBackgroundColorByKey = (rowerKey: string, opacity: number = 0.2): string => {
  const index = parseRowerIndex(rowerKey);
  return getRowerBackgroundColor(index, opacity);
};

/**
 * Semantic colors for non-rower specific chart elements
 * Uses colors from the ROWER_COLORS palette for consistency
 */
export const SEMANTIC_COLORS = {
  speed: ROWER_COLORS[2], // Rowing sky (#3B82F6) - for speed metrics
  distance: ROWER_COLORS[4], // Green (#10B981) - for distance metrics
  acceleration: ROWER_COLORS[5], // Orange/Yellow (#F59E0B) - for acceleration metrics
  power: ROWER_COLORS[0], // Rowing blue (#1E40AF) - for power metrics
  strokeRate: ROWER_COLORS[1], // Rowing red (#DC2626) - for stroke rate metrics
} as const;

/**
 * Get semantic color for chart metrics
 * @param metric - The metric type ('speed', 'distance', 'acceleration', 'power', 'strokeRate')
 * @param format - Color format to return ('hex' or 'rgb')
 * @returns Color string in the requested format
 */
export const getSemanticColor = (
  metric: keyof typeof SEMANTIC_COLORS,
  format: 'hex' | 'rgb' = 'hex'
): string => {
  const hexColor = SEMANTIC_COLORS[metric];

  if (format === 'rgb') {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    return `rgb(${r}, ${g}, ${b})`;
  }

  return hexColor;
};

/**
 * Get semantic background color with opacity
 * @param metric - The metric type
 * @param opacity - Opacity value (0-1), defaults to 0.2
 * @returns RGBA color string
 */
export const getSemanticBackgroundColor = (
  metric: keyof typeof SEMANTIC_COLORS,
  opacity: number = 0.2
): string => {
  const hexColor = SEMANTIC_COLORS[metric];
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Overlay colors for chart plugins and visualizations
 */
export const overlayColors = {
  rangeBackground: 'rgba(68, 94, 147, 0.1)', // Light blue background
  percentileBand: 'rgba(249, 57, 67, 0.15)', // Light red band
  peakMarker: 'rgba(255, 215, 0, 0.8)', // Gold peak marker
  densityGradient: 'rgba(30, 64, 175, 0.2)', // Blue density gradient
} as const;

// ============================================================================
// ACCESSIBILITY FEATURES
// ============================================================================

/**
 * Convert hex color to RGB values
 * @param hex - Hex color string (e.g., '#1E40AF')
 * @returns RGB object with r, g, b values (0-255)
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
};

/**
 * Calculate relative luminance of a color according to WCAG 2.1
 * @param rgb - RGB color object
 * @returns Relative luminance value (0-1)
 */
export const getRelativeLuminance = (rgb: { r: number; g: number; b: number }): number => {
  const { r, g, b } = rgb;

  // Convert to sRGB
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;

  // Apply gamma correction
  const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Calculate contrast ratio between two colors according to WCAG 2.1
 * @param color1 - First color (hex string)
 * @param color2 - Second color (hex string)
 * @returns Contrast ratio (1-21)
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  const lum1 = getRelativeLuminance(rgb1);
  const lum2 = getRelativeLuminance(rgb2);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Check if color combination meets WCAG 2.1 AA contrast requirements
 * @param foreground - Foreground color (hex string)
 * @param background - Background color (hex string)
 * @param level - WCAG level ('AA' or 'AAA')
 * @param size - Text size ('normal' or 'large')
 * @returns Object with compliance status and contrast ratio
 */
export const checkContrastCompliance = (
  foreground: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA',
  size: 'normal' | 'large' = 'normal'
): { compliant: boolean; ratio: number; required: number } => {
  const ratio = getContrastRatio(foreground, background);

  // WCAG 2.1 requirements
  const requirements = {
    AA: { normal: 4.5, large: 3.0 },
    AAA: { normal: 7.0, large: 4.5 },
  };

  const required = requirements[level][size];

  return {
    compliant: ratio >= required,
    ratio: Math.round(ratio * 100) / 100,
    required,
  };
};

/**
 * Get accessible color combinations for rower colors against common backgrounds
 * @param backgroundColor - Background color to test against (defaults to white)
 * @returns Array of rower colors with their accessibility status
 */
export const getAccessibleRowerColors = (
  backgroundColor: string = '#FFFFFF'
): Array<{
  color: string;
  index: number;
  accessible: boolean;
  contrastRatio: number;
}> => {
  return ROWER_COLORS.map((color, index) => {
    const compliance = checkContrastCompliance(color, backgroundColor);
    return {
      color,
      index,
      accessible: compliance.compliant,
      contrastRatio: compliance.ratio,
    };
  });
};

/**
 * Simulate color as perceived by different types of colorblindness
 * @param hex - Original hex color
 * @param type - Type of colorblindness ('protanopia', 'deuteranopia', 'tritanopia')
 * @returns Simulated hex color
 */
export const simulateColorblindness = (
  hex: string,
  type: 'protanopia' | 'deuteranopia' | 'tritanopia'
): string => {
  const rgb = hexToRgb(hex);
  let { r, g, b } = rgb;

  // Convert to 0-1 range
  r /= 255;
  g /= 255;
  b /= 255;

  // Apply colorblindness transformation matrices
  // Based on Brettel, Viénot and Mollon JOSA 14/10 1997
  let newR: number, newG: number, newB: number;

  switch (type) {
    case 'protanopia': // Red-blind
      newR = 0.567 * r + 0.433 * g;
      newG = 0.558 * r + 0.442 * g;
      newB = 0.242 * g + 0.758 * b;
      break;
    case 'deuteranopia': // Green-blind
      newR = 0.625 * r + 0.375 * g;
      newG = 0.7 * r + 0.3 * g;
      newB = 0.3 * g + 0.7 * b;
      break;
    case 'tritanopia': // Blue-blind
      newR = 0.95 * r + 0.05 * g;
      newG = 0.433 * g + 0.567 * b;
      newB = 0.475 * g + 0.525 * b;
      break;
    default:
      newR = r;
      newG = g;
      newB = b;
  }

  // Convert back to 0-255 range and ensure valid values
  const finalR = Math.max(0, Math.min(255, Math.round(newR * 255)));
  const finalG = Math.max(0, Math.min(255, Math.round(newG * 255)));
  const finalB = Math.max(0, Math.min(255, Math.round(newB * 255)));

  // Convert back to hex
  return `#${finalR.toString(16).padStart(2, '0')}${finalG.toString(16).padStart(2, '0')}${finalB.toString(16).padStart(2, '0')}`.toUpperCase();
};

/**
 * Check if rower colors are sufficiently distinct for colorblind users
 * @param colors - Array of hex colors to check
 * @param colorblindType - Type of colorblindness to simulate
 * @param minDistance - Minimum perceptual distance required (0-100)
 * @returns Object with distinctness analysis
 */
export const checkColorblindDistinctness = (
  colors: readonly string[] = ROWER_COLORS,
  colorblindType: 'protanopia' | 'deuteranopia' | 'tritanopia' = 'deuteranopia',
  minDistance: number = 30
): {
  distinct: boolean;
  problematicPairs: Array<{ color1: string; color2: string; distance: number }>;
  simulatedColors: string[];
} => {
  // Simulate colors for the specified colorblindness type
  const simulatedColors = colors.map((color) => simulateColorblindness(color, colorblindType));

  const problematicPairs: Array<{ color1: string; color2: string; distance: number }> = [];

  // Check all pairs for sufficient distinction
  for (let i = 0; i < simulatedColors.length; i++) {
    for (let j = i + 1; j < simulatedColors.length; j++) {
      const rgb1 = hexToRgb(simulatedColors[i]);
      const rgb2 = hexToRgb(simulatedColors[j]);

      // Calculate Euclidean distance in RGB space
      const distance = Math.sqrt(
        Math.pow(rgb1.r - rgb2.r, 2) + Math.pow(rgb1.g - rgb2.g, 2) + Math.pow(rgb1.b - rgb2.b, 2)
      );

      // Convert to 0-100 scale for easier interpretation
      const normalizedDistance = (distance / Math.sqrt(3 * Math.pow(255, 2))) * 100;

      if (normalizedDistance < minDistance) {
        problematicPairs.push({
          color1: colors[i],
          color2: colors[j],
          distance: Math.round(normalizedDistance * 100) / 100,
        });
      }
    }
  }

  return {
    distinct: problematicPairs.length === 0,
    problematicPairs,
    simulatedColors,
  };
};

/**
 * Get the most accessible color combination for a given background
 * @param backgroundColor - Background color (hex string)
 * @param requiredColors - Number of colors needed
 * @param level - WCAG compliance level
 * @returns Array of accessible colors with their compliance info
 */
export const getAccessibleColorCombination = (
  backgroundColor: string = '#FFFFFF',
  requiredColors: number = 4,
  _level: 'AA' | 'AAA' = 'AA'
): Array<{
  color: string;
  index: number;
  accessible: boolean;
  contrastRatio: number;
  colorblindSafe: boolean;
}> => {
  const accessibleColors = getAccessibleRowerColors(backgroundColor);

  // Check colorblind safety for the most common type (deuteranopia)
  const colorblindCheck = checkColorblindDistinctness(
    ROWER_COLORS.slice(0, requiredColors),
    'deuteranopia'
  );

  return accessibleColors.slice(0, requiredColors).map((colorInfo, _index) => ({
    ...colorInfo,
    colorblindSafe: !colorblindCheck.problematicPairs.some(
      (pair) => pair.color1 === colorInfo.color || pair.color2 === colorInfo.color
    ),
  }));
};

/**
 * Validate accessibility of current rower color palette
 * @returns Comprehensive accessibility report
 */
export const validateRowerColorAccessibility = (): {
  wcagCompliance: {
    whiteBackground: Array<{ color: string; compliant: boolean; ratio: number }>;
    darkBackground: Array<{ color: string; compliant: boolean; ratio: number }>;
  };
  colorblindness: {
    protanopia: { distinct: boolean; problematicPairs: number };
    deuteranopia: { distinct: boolean; problematicPairs: number };
    tritanopia: { distinct: boolean; problematicPairs: number };
  };
  recommendations: string[];
} => {
  const whiteBackground = '#FFFFFF';
  const darkBackground = '#1F2937'; // Gray-800 from design system

  const whiteCompliance = getAccessibleRowerColors(whiteBackground);
  const darkCompliance = getAccessibleRowerColors(darkBackground);

  const protanopiaCheck = checkColorblindDistinctness([...ROWER_COLORS], 'protanopia');
  const deuteranopiaCheck = checkColorblindDistinctness([...ROWER_COLORS], 'deuteranopia');
  const tritanopiaCheck = checkColorblindDistinctness([...ROWER_COLORS], 'tritanopia');

  const recommendations: string[] = [];

  // Generate recommendations based on findings
  const nonCompliantWhite = whiteCompliance.filter((c) => !c.accessible);
  const nonCompliantDark = darkCompliance.filter((c) => !c.accessible);

  if (nonCompliantWhite.length > 0) {
    recommendations.push(`${nonCompliantWhite.length} colors fail WCAG AA on white backgrounds`);
  }

  if (nonCompliantDark.length > 0) {
    recommendations.push(`${nonCompliantDark.length} colors fail WCAG AA on dark backgrounds`);
  }

  if (!protanopiaCheck.distinct) {
    recommendations.push(
      `${protanopiaCheck.problematicPairs.length} color pairs are too similar for protanopia users`
    );
  }

  if (!deuteranopiaCheck.distinct) {
    recommendations.push(
      `${deuteranopiaCheck.problematicPairs.length} color pairs are too similar for deuteranopia users`
    );
  }

  if (!tritanopiaCheck.distinct) {
    recommendations.push(
      `${tritanopiaCheck.problematicPairs.length} color pairs are too similar for tritanopia users`
    );
  }

  if (recommendations.length === 0) {
    recommendations.push('All colors meet accessibility requirements');
  }

  return {
    wcagCompliance: {
      whiteBackground: whiteCompliance.map((c) => ({
        color: c.color,
        compliant: c.accessible,
        ratio: c.contrastRatio,
      })),
      darkBackground: darkCompliance.map((c) => ({
        color: c.color,
        compliant: c.accessible,
        ratio: c.contrastRatio,
      })),
    },
    colorblindness: {
      protanopia: {
        distinct: protanopiaCheck.distinct,
        problematicPairs: protanopiaCheck.problematicPairs.length,
      },
      deuteranopia: {
        distinct: deuteranopiaCheck.distinct,
        problematicPairs: deuteranopiaCheck.problematicPairs.length,
      },
      tritanopia: {
        distinct: tritanopiaCheck.distinct,
        problematicPairs: tritanopiaCheck.problematicPairs.length,
      },
    },
    recommendations,
  };
};

// Debug: Expose color functions to window for staging verification
if (typeof window !== 'undefined' && process.env.REACT_APP_SENTRY_ENVIRONMENT === 'staging') {
  (window as any).debugRowerColors = {
    getRowerColorByKey,
    getRowerBackgroundColorByKey,
    ROWER_COLORS,
    testColors: () => {
      // eslint-disable-next-line no-console
      console.log('🎨 ROWER COLOR SYSTEM VERIFICATION:');
      // eslint-disable-next-line no-console
      console.log('Rower 1:', getRowerColorByKey('Rower 1'), '(should be #1E40AF - Blue)');
      // eslint-disable-next-line no-console
      console.log('Rower 2:', getRowerColorByKey('Rower 2'), '(should be #DC2626 - Red)');
      // eslint-disable-next-line no-console
      console.log('Rower 3:', getRowerColorByKey('Rower 3'), '(should be #3B82F6 - Sky)');
      // eslint-disable-next-line no-console
      console.log('Rower 4:', getRowerColorByKey('Rower 4'), '(should be #991B1B - Crimson)');
      // eslint-disable-next-line no-console
      console.log('✅ Color system is working correctly!');
    },
  };
}
